import React from 'react';
import { Button, TextBlock } from 'bench-internal-theme';
import { LedgerAccount } from 'general-ledger/fast-gl/types';
import {
  LedgerAccountHeaderLayout,
  LedgerDescriptionLayout,
  LedgerNameLayout,
  LedgerNameButton,
  NoteText,
  BalancesLayout,
} from './LedgerAccountHeader.styles';
import FloatingExpandableText from '../../../FloatingExpandableText';
import { isBalanceSheet } from 'general-ledger/fast-gl/utils/ledgerDisplay';
import { formatAmountForDisplay } from 'general-ledger/fast-gl/utils/currency';
import { useGeneralLedger } from 'general-ledger/fast-gl/hooks/useGeneralLedger';
import { removeLeadingAndTrailingQuotes } from '../DataEntryTableColumnDefinitions';
import { HighlightKeyword } from 'bench-internal-theme';
import { BalanceRefreshButton } from './BalanceRefreshButton';

type LedgerAccountHeaderProps = {
  ledgerAccount: LedgerAccount;
};

export const LedgerAccountHeader = ({
  ledgerAccount,
}: LedgerAccountHeaderProps): React.ReactElement => {
  const hasDetails = ledgerAccount.details;
  const hasNotes = ledgerAccount.internalNotes?.length;
  const hasComments = hasDetails || hasNotes;
  const { updateSelectedLedgerAccount, search } = useGeneralLedger();

  const handleLedgerClick = () =>
    updateSelectedLedgerAccount && updateSelectedLedgerAccount(ledgerAccount);

  return (
    <LedgerAccountHeaderLayout>
      <LedgerDescriptionLayout>
        <LedgerNameLayout>
          <LedgerNameButton onClick={handleLedgerClick} data-bypass-dismiss-sidebar>
            {ledgerAccount.name}
          </LedgerNameButton>
          {hasComments ? (
            <div>
              <Button
                data-bypass-dismiss-sidebar
                icon="notebook"
                size="xsmall"
                styleType="total-ghost"
                title="This ledger has a description or internal notes"
              />
            </div>
          ) : null}
        </LedgerNameLayout>
        {hasDetails ? (
          <NoteText>
            <FloatingExpandableText>{ledgerAccount.details}</FloatingExpandableText>
          </NoteText>
        ) : null}
        {hasNotes
          ? ledgerAccount.internalNotes.map(({ body }) => (
              <NoteText key={body}>
                <FloatingExpandableText>
                  Internal Note:&nbsp;
                  <HighlightKeyword keyword={removeLeadingAndTrailingQuotes(search)}>
                    {body}
                  </HighlightKeyword>
                </FloatingExpandableText>
              </NoteText>
            ))
          : null}
      </LedgerDescriptionLayout>
      <BalancesLayout>
        {isBalanceSheet(ledgerAccount) ? (
          <>
            <TextBlock styleType="small">Opening Balance:</TextBlock>
            <TextBlock align="right" styleType="small">
              {formatAmountForDisplay(
                ledgerAccount,
                ledgerAccount.accountingPeriodBalance.openingBalance,
              )}
            </TextBlock>
            <BalanceRefreshButton ledgerAccount={ledgerAccount} />
            <TextBlock styleType="small">Closing Balance:</TextBlock>
            <TextBlock align="right" styleType="small">
              {formatAmountForDisplay(
                ledgerAccount,
                ledgerAccount.accountingPeriodBalance.closingBalance,
              )}
            </TextBlock>
            <BalanceRefreshButton ledgerAccount={ledgerAccount} />
          </>
        ) : (
          <>
            <TextBlock styleType="small">Total:</TextBlock>
            <TextBlock align="right" styleType="small">
              {formatAmountForDisplay(
                ledgerAccount,
                ledgerAccount.accountingPeriodBalance.inPeriodBalance,
              )}
            </TextBlock>
            <BalanceRefreshButton ledgerAccount={ledgerAccount} />
          </>
        )}
      </BalancesLayout>
    </LedgerAccountHeaderLayout>
  );
};
